import { Capacitor } from '@capacitor/core';
import InAppCheckout from '../plugins/inAppCheckout';

export { cartPathFromCheckoutExit, isCheckoutCancelUrl, isCheckoutExitUrl } from './mpgsCheckoutUrls';

declare global {
  interface Window {
    Checkout?: {
      configure: (config: Record<string, any>) => void;
      showPaymentPage: () => void;
      showEmbeddedPage?: (selector: string) => void;
    };
    qhMpgsError?: () => void;
    qhMpgsCancel?: () => void;
    qhMpgsComplete?: () => void;
  }
}

export const MPGS_PAY_LAUNCH_KEY = 'mpgsPayLaunch';

export type MpgsPayLaunch = {
  sessionId?: string;
  scriptUrls: string[];
  gatewayOrderId: string;
  checkoutUrl?: string | null;
};

export function readMpgsPayLaunch(): MpgsPayLaunch | null {
  try {
    const raw = sessionStorage.getItem(MPGS_PAY_LAUNCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MpgsPayLaunch;
    if (!parsed?.checkoutUrl && !parsed?.sessionId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeMpgsPayLaunch(launch: MpgsPayLaunch) {
  sessionStorage.setItem(MPGS_PAY_LAUNCH_KEY, JSON.stringify(launch));
}

export function resetMpgsCheckoutScript() {
  try {
    delete window.Checkout;
  } catch {
    window.Checkout = undefined;
  }
  document.querySelectorAll('script[data-qh-mpgs="true"]').forEach((el) => el.remove());
}

export function loadMpgsCheckoutScript(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Checkout) {
      resolve();
      return;
    }
    const timeoutId = window.setTimeout(() => {
      reject(new Error('Payment script timed out.'));
    }, 15000);
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.dataset.qhMpgs = 'true';
    script.setAttribute('data-error', 'qhMpgsError');
    script.setAttribute('data-cancel', 'qhMpgsCancel');
    script.setAttribute('data-complete', 'qhMpgsComplete');
    script.onload = () => {
      window.clearTimeout(timeoutId);
      if (window.Checkout) resolve();
      else reject(new Error('Payment script loaded without Checkout.'));
    };
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      reject(new Error('Could not load the payment form.'));
    };
    document.body.appendChild(script);
  });
}

export async function loadFirstWorkingCheckoutScript(urls: string[]): Promise<string> {
  let lastError: Error | null = null;
  for (const url of urls) {
    resetMpgsCheckoutScript();
    try {
      await loadMpgsCheckoutScript(url);
      return url;
    } catch (err: any) {
      lastError = err instanceof Error ? err : new Error(String(err?.message || err));
    }
  }
  throw lastError || new Error('Could not load the payment form.');
}

export function embedHasCheckoutUi(container: HTMLElement | null): boolean {
  if (!container) return false;
  return Boolean(container.querySelector('iframe, input, select, textarea'));
}

export async function openHostedCheckoutUrl(
  url: string
): Promise<{ status: 'return' | 'cancel'; url: string }> {
  if (Capacitor.isNativePlatform()) {
    return InAppCheckout.open({ url });
  }
  window.location.assign(url);
  return { status: 'return', url };
}
